import type { Novu } from '@novu/js';
import { ref, type Ref, type ShallowRef } from 'vue';

import type { AppNotification } from '../../types/notifications.types';

interface UseNotificationsReadModuleConfig {
  novu: Readonly<ShallowRef<Novu | null>>;
  notifications: Ref<AppNotification[]>;
  unreadCount: Ref<number>;
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
}

export const useNotificationsReadModule = (config: UseNotificationsReadModuleConfig) => {
  const isLocalReadInFlight = ref(false);

  const handleOptimisticReadError = async (error: unknown) => {
    console.error('[NotificationsStore] Failed to mark as read:', error);
    await config.fetchNotifications();
    await config.fetchUnreadCount();
  };

  const withOptimisticRead = async (action: () => Promise<{ error?: unknown }>) => {
    isLocalReadInFlight.value = true;
    try {
      const { error } = await action();

      if (error) {
        await handleOptimisticReadError(error);
      }
    } catch (error) {
      await handleOptimisticReadError(error);
    } finally {
      isLocalReadInFlight.value = false;
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!config.novu.value) {
      return;
    }

    const targetNotification = config.notifications.value.find(
      (notification) => notification.id === notificationId,
    );

    if (!targetNotification || targetNotification.read) {
      return;
    }

    config.notifications.value = config.notifications.value.map((notification) => {
      if (notification.id !== notificationId || notification.read) {
        return notification;
      }

      return { ...notification, read: true };
    });
    config.unreadCount.value = Math.max(0, config.unreadCount.value - 1);

    await withOptimisticRead(() => config.novu.value!.notifications.read({ notificationId }));
  };

  const markAllAsRead = async () => {
    if (!config.novu.value) {
      return;
    }

    config.notifications.value = config.notifications.value.map((notification) => {
      if (notification.read) {
        return notification;
      }

      return { ...notification, read: true };
    });
    config.unreadCount.value = 0;

    await withOptimisticRead(() => config.novu.value!.notifications.readAll());
  };

  return {
    markAsRead,
    markAllAsRead,
    isLocalReadInFlight,
  };
};
